import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowRight, X, Tag } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";

const ARTICLES = [
  {
    id: "esp32-3phase",
    category: "Hardware",
    title: "Building a 3-Phase Energy Monitor with ESP32 & PZEM-004T",
    date: "12 Jun 2026",
    read: "8 min",
    image: "/assets/hw-internal.jpeg",
    excerpt: "How three PZEM-004T meters, a single ESP32 and a handful of CT clamps come together into a campus-grade energy logger.",
    body: [
      "Metering three phases independently is the heart of this project. Each PZEM-004T module measures voltage, current, active power, power factor, frequency and accumulated energy for one line - L1, L2 and L3.",
      "The ESP32 talks to all three meters over a shared Modbus-RTU bus on a single UART, addressing each module individually. Readings are sampled twice a second and published to HiveMQ Cloud as compact JSON payloads.",
      "Split-core current transformers clamp around each live conductor without breaking the circuit, making installation safe and non-invasive. A DS18B20 temperature probe and an MQ-135 gas sensor monitor the enclosure environment - the firmware ramps the cooling fan based on temperature and triggers a buzzer alert when air quality hits DANGER (CO₂/NH₃/smoke above threshold).",
      "The result is a reliable, low-cost node that streams a full electrical picture of a building's supply - the foundation everything else in the dashboard is built on.",
    ],
  },
  {
    id: "mqtt-hivemq",
    category: "Architecture",
    title: "Why MQTT and HiveMQ Cloud for IoT Telemetry",
    date: "10 Jun 2026",
    read: "6 min",
    image: "/assets/diag-mqtt.png",
    excerpt: "Publish/subscribe beats polling for constrained devices. Here's how the topic design keeps the system responsive and cheap.",
    body: [
      "MQTT's publish/subscribe model is a natural fit for IoT: the ESP32 publishes telemetry to topics, and any number of dashboards subscribe without the device ever knowing they exist.",
      "We use a clean topic hierarchy - campus/esp32/telemetry for readings and campus/esp32/cmd for control - so commands and data never collide. QoS 1 guarantees delivery of control messages like ALL-OFF.",
      "HiveMQ Cloud gives us a managed, TLS-secured broker with a generous free tier, so there's no server to maintain. The same broker fans data out to Grafana, ThingSpeak and this dashboard simultaneously.",
    ],
  },
  {
    id: "enclosure-design",
    category: "Design",
    title: "Designing the Enclosure: From CAD to a Cape Town Workshop",
    date: "8 Jun 2026",
    read: "5 min",
    image: "/assets/hw-front.jpeg",
    excerpt: "A walkthrough of the custom housing - LCD window, PIR cut-out, camera port and ventilated base.",
    body: [
      "A good enclosure makes a prototype look like a product. The front panel hosts the 20×4 LCD, the PIR dome, the camera and the light hub - all aligned on a single clean face.",
      "The rear carries an 80mm cooling fan with 9V and 12V inputs, while the base is fully ventilated with rubber feet for bench or DIN-rail mounting.",
      "Every cut-out was modelled in CAD first, then refined against the real boards before the housing was finished in a local Cape Town workshop.",
    ],
  },
  {
    id: "ai-assistant",
    category: "Software",
    title: "Adding an AI Assistant to an Energy Dashboard",
    date: "5 Jun 2026",
    read: "7 min",
    image: "/assets/laptop-dash.png",
    excerpt: "Natural-language queries, slash commands and voice/video modes - making energy data conversational.",
    body: [
      "Operators shouldn't need to read a manual to control hardware. The AI assistant turns plain questions like 'what's the current temperature?' into instant answers drawn from live telemetry.",
      "Slash commands such as /fan auto and /light on map directly to MQTT control messages, giving power users a fast keyboard-driven workflow.",
      "Voice and video call modes push it further - speak to the system hands-free, or point a camera at equipment for visual context. It's a glimpse of how IoT control rooms will feel.",
    ],
  },
  {
    id: "cloudflare-deploy",
    category: "DevOps",
    title: "Deploying to Cloudflare Pages: Lessons Learned",
    date: "2 Jun 2026",
    read: "4 min",
    image: "/assets/hw-panel.png",
    excerpt: "Static-first, globally distributed, and free - why Cloudflare Pages was the right home for the frontend.",
    body: [
      "The dashboard is a single-page app, which makes it perfect for static hosting. Cloudflare Pages serves it from a global edge network, so it loads fast whether you're in Cape Town or Cambridge.",
      "Because the device talks to the cloud broker directly, the frontend stays completely static - no backend to scale, no servers to patch.",
      "Continuous deployment from git means every push ships to production in under a minute.",
    ],
  },
  {
    id: "power-factor-101",
    category: "Education",
    title: "Power Factor 101: Reading Your Campus Energy Data",
    date: "28 May 2026",
    read: "6 min",
    image: "/assets/bom-cost.jpeg",
    excerpt: "What power factor really tells you, and why a campus with lots of motors should care about it.",
    body: [
      "Power factor is the ratio of real power (that does useful work) to apparent power (what the grid must supply). A value near 1.0 means efficient use; lower values mean wasted capacity.",
      "Inductive loads - motors, pumps, HVAC - drag power factor down. Utilities often penalise large consumers for poor power factor, so monitoring it across three phases can translate directly into savings.",
      "The dashboard surfaces per-phase power factor live, so facilities teams can spot a drifting plant room before it shows up on the bill.",
    ],
  },
];

const fade = { hidden: { opacity: 0, y: 20 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45 } }) };

export default function Publications() {
  const [open, setOpen] = useState(null);
  const featured = ARTICLES[0];
  const rest = ARTICLES.slice(1);

  return (
    <PublicLayout>
      <section className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Publications</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Field notes & write-ups</h1>
          <p className="text-[var(--t2)] mt-4">Engineering deep-dives, design decisions and lessons from building the Campus Energy Monitor.</p>
        </div>

        {/* Featured */}
        <motion.button initial="hidden" animate="show" variants={fade} onClick={() => setOpen(featured)} data-testid="publication-featured"
          className="group w-full text-left grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors mb-10">
          <div className="aspect-video lg:aspect-auto overflow-hidden bg-black">
            <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
          <div className="p-6 sm:p-10 flex flex-col justify-center">
            <div className="flex items-center gap-3 text-xs text-[var(--t2)] mb-3">
              <span className="px-2.5 py-1 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-mono">{featured.category}</span>
              <span className="flex items-center gap-1"><Calendar size={12} />{featured.date}</span>
              <span className="flex items-center gap-1"><Clock size={12} />{featured.read}</span>
            </div>
            <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{featured.title}</h2>
            <p className="text-[var(--t2)] mt-3 leading-relaxed">{featured.excerpt}</p>
            <span className="inline-flex items-center gap-1.5 text-[var(--accent)] font-medium mt-5">Read article <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
          </div>
        </motion.button>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rest.map((a, i) => (
            <motion.button key={a.id} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} onClick={() => setOpen(a)} data-testid={`publication-card-${a.id}`}
              className="group text-left rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors flex flex-col">
              <div className="aspect-video overflow-hidden bg-black">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] text-[var(--t2)] mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-mono">{a.category}</span>
                  <span className="flex items-center gap-1"><Clock size={11} />{a.read}</span>
                </div>
                <h3 className="font-heading font-bold text-lg leading-snug">{a.title}</h3>
                <p className="text-sm text-[var(--t2)] mt-2 leading-relaxed flex-1">{a.excerpt}</p>
                <span className="text-xs text-[var(--t2)] font-mono mt-4 flex items-center gap-1"><Calendar size={11} />{a.date}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Reader modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto" onClick={() => setOpen(null)} data-testid="publication-reader">
          {/* Always-visible close button fixed to viewport */}
          <button onClick={() => setOpen(null)} data-testid="publication-close" style={{ position: "fixed", top: 16, right: 16, zIndex: 60 }} className="w-11 h-11 rounded-full bg-black/80 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shadow-xl"><X size={20} /></button>
          <article className="bg-[var(--bg)] border border-[var(--bd)] sm:rounded-2xl w-full max-w-2xl my-0 sm:my-8 overflow-hidden fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video overflow-hidden bg-black">
              <img src={open.image} alt={open.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-3 text-xs text-[var(--t2)] mb-3">
                <span className="px-2.5 py-1 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] font-mono flex items-center gap-1"><Tag size={11} />{open.category}</span>
                <span className="flex items-center gap-1"><Calendar size={12} />{open.date}</span>
                <span className="flex items-center gap-1"><Clock size={12} />{open.read}</span>
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{open.title}</h2>
              <div className="mt-5 space-y-4">
                {open.body.map((p, i) => <p key={i} className="text-[var(--t2)] leading-relaxed">{p}</p>)}
              </div>
            </div>
          </article>
        </div>
      )}
    </PublicLayout>
  );
}
